import { useState, useRef, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { RxCross2 } from "react-icons/rx";
import {
    FaUser, FaShoppingCart, FaPlus, FaBox, FaTrash, FaShieldAlt,
    FaWarehouse, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaHashtag, FaCity,
    FaFlag, FaRupeeSign, FaWallet, FaClipboardList, FaWeightHanging, FaBoxOpen,
    FaRulerCombined, FaFileInvoice, FaChevronDown, FaCheckCircle, FaLock
} from 'react-icons/fa';
import { useMyContext } from '../../../context/MyContext';
import '../../../css/custom-scrollbar.css';
import B2BViewRateForword from '../../../component/b2b/models/priceEstimatorModelB2b';
import { Base_Url, b2bAddShipment, getB2bpIncode, b2bWarehouseListAll } from '../../../config/config';
import { toast } from 'react-toastify';
import Spinner from '../../../component/spinner/spinner';
import axios from 'axios';
import B2BUploadBulk from '../../../component/b2b/models/uploadBulkModel';
import * as pdfjsLib from "pdfjs-dist";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

const schema = z.object({
    warehouse: z.string().optional(),
    firstName: z.string().min(1, "First Name is required").regex(/^[a-zA-Z\s]+$/, "First Name must only contain alphabetic characters"),
    lastName: z.string().min(1, "Last Name is required").regex(/^[a-zA-Z\s]+$/, "Last Name must only contain alphabetic characters"),
    email: z.string().email("Invalid email format").optional().or(z.literal("")),
    mobile: z.string()
        .length(10, "Mobile number must be exactly 10 digits")
        .regex(/^\d+$/, "Mobile number must be numeric"),
    address: z
        .string()
        .trim()
        .min(10, "Full Address must be at least 10 characters")
        .regex(/^[^#\\%&;]*$/, "Address contains invalid characters (# \\ % & ; not allowed)")
        .refine((val) => val.trim().length >= 10, {
            message: "Address cannot be only spaces",
        }),
    pincode: z.string()
        .length(6, "Pincode should be exactly 6 characters")
        .regex(/^\d+$/, "Pincode must be numeric"),
    city: z.string().min(1, "City is required").regex(/^[a-zA-Z\s]+$/, "City must only contain alphabetic characters"),
    state: z.string().min(1, "State is required").regex(/^[a-zA-Z\s]+$/, "State must only contain alphabetic characters"),
    orderId: z.string().min(1, "Order ID is required"),
    invoiceAmount: z.string()
        .min(1, "Invoice Amount is required")
        .regex(/^\d+(\.\d+)?$/, "Invoice Amount must be numeric"),
    invoiceNumber: z.string()
        .min(1, "Invoice number is required")
        .regex(/^[A-Za-z0-9\/-]*$/, "Invoice number must only contain letters, numbers, / or -"),
    eWaybillNo: z.string()
        .max(12, "E-Waybill number must be at most 12 characters")
        .optional(),
    paymentMode: z.string().min(1, "Payment Mode is required"),
    productDes: z.string().min(1, "Product Description is required"),
    codAmount: z.string().optional(),
    insurence: z.string().optional(),
    total_weight: z.string()
        .min(1, "Total Weight is required")
        .regex(/^\d*\.?\d+$/, "Total Weight must be a positive number and can be a float"),
    length: z.string()
        .regex(/^\d*\.?\d+$/, "Length must be a positive number and can be a float")
        .optional(),
    width: z.string()
        .regex(/^\d*\.?\d+$/, "Width must be a positive number and can be a float")
        .optional(),
    height: z.string()
        .regex(/^\d*\.?\d+$/, "Height must be a positive number and can be a float")
        .optional(),
}).refine(
    (data) => {
        const amount = parseFloat(data.invoiceAmount);
        if (amount > 50000) {
            return !!data.eWaybillNo && data.eWaybillNo.trim() !== "";
        }
        return true;
    },
    {
        message: "E-Waybill number is required when Invoice Amount is greater than 50000",
        path: ["eWaybillNo"],
    }
);

const errorText = (msg) => (
    <p className="text-rose-500 dark:text-rose-400 text-[10px] font-medium mt-1 flex items-center gap-1 animate-[fadeIn_0.2s_ease-out]">
        <span className="w-1 h-1 rounded-full bg-rose-500 shrink-0" />
        <span>{msg}</span>
    </p>
);

const Field = ({ icon: Icon, label, error, required, endAdornment, children, htmlFor, isDarkMode }) => (
    <div className="flex flex-col">
        {label && (
            <label htmlFor={htmlFor} className={`flex items-center justify-between text-[11px] font-semibold mb-1 ${
                isDarkMode ? "text-slate-300" : "text-slate-700"
            }`}>
                <span className="flex items-center gap-1">
                    {label}
                    {required && <span className="text-rose-500 font-bold">*</span>}
                </span>
                {endAdornment}
            </label>
        )}
        <div className="relative group">
            {Icon && (
                <Icon
                    size={11}
                    className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                        ${isDarkMode ? "text-slate-500 group-focus-within:text-indigo-400" : "text-slate-400 group-focus-within:text-indigo-600"}`}
                />
            )}
            {children}
        </div>
        {error && errorText(error)}
    </div>
);

const SectionCard = ({ number, icon: Icon, iconGradient, title, subtitle, badge, children, className = "", isDarkMode }) => (
    <div
        className={`rounded-2xl border p-4 transition-all duration-200 ${
            isDarkMode
                ? "bg-[#121524]/90 border-white/[0.08] shadow-[0_4px_24px_rgba(0,0,0,0.25)] hover:border-indigo-500/30"
                : "bg-white border-slate-200/90 shadow-[0_2px_12px_rgba(0,0,0,0.03)] hover:border-slate-300/90 hover:shadow-[0_4px_16px_rgba(0,0,0,0.05)]"
        } ${className}`}
    >
        <div className="flex items-center justify-between pb-3 mb-3.5 border-b border-slate-100 dark:border-white/[0.06]">
            <div className="flex items-center gap-2.5">
                <span className={`w-7 h-7 rounded-xl flex items-center justify-center text-white shadow-sm shrink-0 ${iconGradient || 'bg-gradient-to-tr from-indigo-500 to-violet-600'}`}>
                    <Icon size={12} />
                </span>
                <div>
                    <h2 className={`text-xs font-bold tracking-tight ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                        {title}
                    </h2>
                    {subtitle && (
                        <p className={`text-[10px] font-medium ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>
            {badge && (
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    isDarkMode ? "bg-white/5 text-slate-400" : "bg-slate-100 text-slate-600"
                }`}>
                    {badge}
                </span>
            )}
        </div>
        <div>{children}</div>
    </div>
);

// Searchable Warehouse Dropdown Component
const SearchableWarehouseSelect = ({
    value,
    onChange,
    options,
    isDarkMode,
    error,
    placeholder = "Search & select warehouse..."
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption ? selectedOption.label : '';

    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opt.value.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
        (opt.pincode && String(opt.pincode).includes(searchTerm))
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option) => {
        onChange(option.value);
        setIsOpen(false);
        setSearchTerm('');
    };

    const openDropdown = () => {
        setIsOpen(true);
        setTimeout(() => {
            inputRef.current?.focus();
        }, 50);
    };

    const handleInputClick = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
        }
    };

    const inputBase = `w-full pl-8 pr-8 py-2 text-xs font-medium rounded-xl border transition-all duration-200 outline-none
        placeholder:text-slate-400 dark:placeholder:text-slate-500 cursor-pointer
        ${isDarkMode
            ? "bg-white/[0.04] border-white/10 text-white focus:bg-[#161a29] focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 hover:border-white/20"
            : "bg-slate-50/80 border-slate-200 text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15 hover:border-slate-300"
        }`;

    return (
        <div className="relative" ref={dropdownRef}>
            <div className="relative group">
                <FaWarehouse
                    size={11}
                    className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 transition-colors duration-200 pointer-events-none
                        ${isDarkMode ? "text-slate-500 group-focus-within:text-indigo-400" : "text-slate-400 group-focus-within:text-indigo-600"}`}
                />
                <input
                    ref={inputRef}
                    type="text"
                    value={isOpen ? searchTerm : displayValue}
                    onChange={(e) => {
                        if (isOpen) {
                            setSearchTerm(e.target.value);
                        }
                    }}
                    onFocus={openDropdown}
                    onClick={openDropdown}
                    placeholder={placeholder}
                    className={`${inputBase} ${error ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                    readOnly={!isOpen}
                />
                <button
                    type="button"
                    onClick={handleInputClick}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors pointer-events-none ${
                        isDarkMode ? "text-slate-500" : "text-slate-400"
                    }`}
                >
                    <FaChevronDown
                        size={9}
                        className={`transition-transform duration-200 ${isOpen ? 'rotate-180 text-indigo-500' : ''}`}
                    />
                </button>
            </div>

            {isOpen && (
                <div
                    className={`absolute z-[10000] w-full mt-1.5 max-h-48 overflow-y-auto rounded-xl border shadow-xl custom-scrollbar animate-[fadeIn_0.15s_ease-out]
                        ${isDarkMode
                            ? "bg-[#141829] border-white/10 shadow-black/60"
                            : "bg-white border-slate-200 shadow-slate-300/50"
                        }`}
                >
                    <div className={`px-3 py-1.5 border-b text-[9.5px] font-bold uppercase tracking-wider flex items-center justify-between ${
                        isDarkMode ? 'border-white/[0.06] text-slate-400 bg-white/[0.02]' : 'border-slate-100 text-slate-500 bg-slate-50/70'
                    }`}>
                        <span>Select Warehouse</span>
                        <span className="text-[9px] font-mono opacity-70">{filteredOptions.length} available</span>
                    </div>
                    {filteredOptions.length === 0 ? (
                        <div className={`px-3 py-4 text-center text-xs ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                            No matching warehouses found
                        </div>
                    ) : (
                        filteredOptions.map((option) => (
                            <div
                                key={option.value}
                                onClick={() => handleSelect(option)}
                                className={`px-3 py-2 text-xs cursor-pointer transition-colors flex items-center justify-between gap-2
                                    ${option.value === value
                                        ? (isDarkMode ? "bg-indigo-600/30 font-semibold text-indigo-300" : "bg-indigo-50 font-semibold text-indigo-600")
                                        : (isDarkMode ? "hover:bg-indigo-600/20 text-slate-200" : "hover:bg-indigo-50/80 text-slate-700")
                                    }`}
                            >
                                <div className="truncate font-medium">{option.label}</div>
                                {option.pincode && (
                                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded shrink-0 ${
                                        isDarkMode ? "bg-white/10 text-slate-300" : "bg-slate-100 text-slate-600"
                                    }`}>
                                        PIN: {option.pincode}
                                    </span>
                                )}
                            </div>
                        ))
                    )}
                </div>
            )}
            {error && errorText(error)}
        </div>
    );
};

export default function B2BForwordShipment({ isOpen, onClose, onCreated }) {
    const toastOpts = onClose ? { containerId: 'b2b-toast-container', position: "top-right" } : undefined;
    const formatField = (key) => String(key)
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    const [formErrors, setFormErrors] = useState([]);
    const showFormError = (msg, field) => {
        const text = Array.isArray(msg) ? msg.join(', ') : (msg || 'Something went wrong');
        const full = field ? `${formatField(field)}: ${text}` : text;
        setFormErrors((prev) => (prev.includes(full) ? prev : [...prev, full]));
        toast.error(full, toastOpts);
    };
    const [invoiceFileName, setInvoiceFileName] = useState('');
    const [secondaryFile, setSecondaryFile] = useState('');
    const { isDarkMode, selectedCft } = useMyContext();
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [uploadBulkPopupOpen, setUploadBulkPopupOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [tableData, setTableData] = useState(null);
    const Navigation = useNavigate();
    const [wareHouseList, setwareHouseList] = useState([]);
    const [wareHouseId, setwareHouseId] = useState('');
    const [wareHousePincode, setwareHousePincode] = useState('');
    const [reqBody, setReqBody] = useState([]);
    const [totalBoxes, setTotalBoxes] = useState(0);
    const [selectedUnit, setSelectedUnit] = useState('cm');
    const [boxSets, setBoxSets] = useState([]);
    const [remainingBoxes, setRemainingBoxes] = useState(0);
    const [file, setFile] = useState(null);
    const [sec_file, set_Sec_File] = useState(null);
    const [lrnOption, setLrnOption] = useState('auto');
    const [manualLrn, setManualLrn] = useState('');
    const [phoneResults, setPhoneResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loadingPhone, setLoadingPhone] = useState(false);

    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    const { register, handleSubmit, formState: { errors }, reset, trigger, watch, setValue } = useForm({
        resolver: zodResolver(schema),
    });

    const handleClose = () => {
        setIsPopupOpen(false);
    };

    const handleCloseUploadBulk = () => {
        setUploadBulkPopupOpen(false);
    };

    const pincode = watch("pincode");
    const city = watch("city");
    const state = watch("state");

    useEffect(() => {
        if (pincode && pincode.length === 6) {
            fetchPincodeData(pincode);
        }
    }, [pincode]);

    const fetchPincodeData = async (pincode) => {
        const token = localStorage.getItem('token');
        const cus_id = localStorage.getItem('cus_id');
        const urlgetPincode = `${Base_Url}${getB2bpIncode}?pincode=${pincode}`;
        try {
            const response = await fetch(urlgetPincode, {
                method: 'GET',
                headers: {
                    'token': token,
                    'cus_id': cus_id,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            if (data && data.status === "SUCCESS") {
                let { city, state } = data.data;
                city = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
                state = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
                setValue("city", city);
                setValue("state", state);
            } else if (data && data.message === "Unauthorized access - Invalid token") {
                toast.error(data.message + " Please login Again");
            } else if (data && data.status === "FAIL") {
                toast.error("Something Went Wrong Please Try Again With Another Pincode");
            } else {
                toast.error("Failed to fetch city and state data");
            }
        } catch (error) {
            console.error('Error fetching pincode data:', error);
            toast.error("Error fetching pincode data");
        }
    };

    const paymentMode = watch("paymentMode");

    useEffect(() => {
        if (paymentMode === "prepaid") {
            setValue("codAmount", "0");
        } else if (paymentMode === "cod") {
            setValue("codAmount", "");
        }
    }, [paymentMode, setValue]);

    const handleInputChange = (event) => {
        const { value, id } = event.target;
        if (['pincode', 'mobile', 'boxQty'].includes(id)) {
            const filteredValue = value.replace(/[^0-9]/g, '');
            event.target.value = filteredValue;
        }
        if (['firstName', 'lastName', 'city', 'state'].includes(id)) {
            const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
            event.target.value = filteredValue;
        }
    };

    useEffect(() => {
        if (isOpen) { setFormErrors([]); fetchWareHouseList(); }
    }, [isOpen]);

    const fetchWareHouseList = async () => {
        const token = localStorage.getItem('token');
        const cusid = localStorage.getItem('cus_id');
        const headers = new Headers({
            'token': token,
            'cus_id': cusid,
            'Content-Type': 'application/json'
        });

        try {
            const response = await fetch(Base_Url + b2bWarehouseListAll, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const transformedData = data.data.map(warehouse => ({
                value: warehouse.warehouse_id,
                label: warehouse.name,
                pincode: warehouse.pincode
            }));
            setwareHouseList(transformedData);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const onSubmit = async (data) => {
        setFormErrors([]);
        setIsSubmitting(true);

        if (boxSets && boxSets.length > 0) {
            const invalidBoxSet = boxSets.find(box =>
                !box.quantity || !box.length || !box.width || !box.height ||
                box.quantity <= 0 || box.length <= 0 || box.width <= 0 || box.height <= 0
            );

            if (invalidBoxSet) {
                showFormError("Please fill all box set dimensions with positive values", "Box Sets");
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const formData = new FormData();

            formData.append('warehouse_id', wareHouseId || '');
            formData.append('type', selectedCft || '');
            formData.append('total_boxes', totalBoxes || '');
            formData.append('payment_mode', data.paymentMode || '');
            formData.append('cod_amount', data.codAmount || '0');
            formData.append('total_weight', data.total_weight || '');

            const dropoffLocation = {
                consignee_name: `${data.firstName} ${data.lastName}`,
                address: data.address || '',
                city: data.city || '',
                state: data.state || '',
                zip: data.pincode || '',
                phone: data.mobile || '',
                email: data.email || '',
            };
            formData.append('dropoff_location', JSON.stringify(dropoffLocation));

            const invoices = [{
                ewaybill: data.eWaybillNo,
                inv_num: data.invoiceNumber,
                inv_amt: data.invoiceAmount,
                inv_qr_code: ""
            }];
            formData.append('invoices', JSON.stringify(invoices));

            const shipmentDetails = [{
                order_id: data.orderId || '',
                box_count: totalBoxes || 1,
                description: data.productDes || '',
                weight: parseFloat(data.total_weight) || 0,
                waybills: [],
                master: "False"
            }];
            formData.append('shipment_details', JSON.stringify(shipmentDetails));

            const dimensions = boxSets && boxSets.length > 0
                ? boxSets.map(boxSet => ({
                    box_count: parseInt(boxSet.quantity) || 1,
                    length: parseFloat(boxSet.length) || 0,
                    width: parseFloat(boxSet.width) || 0,
                    height: parseFloat(boxSet.height) || 0
                }))
                : [{
                    box_count: 1,
                    length: parseFloat(data.length) || 0,
                    width: parseFloat(data.width) || 0,
                    height: parseFloat(data.height) || 0
                }];
            formData.append('dimensions', JSON.stringify(dimensions));

            const docData = [{
                doc_type: "INVOICE_COPY",
                doc_meta: {
                    invoice_num: [data.invoiceNumber || data.orderId || '']
                }
            }];
            formData.append('doc_data', JSON.stringify(docData));

            if (file) formData.append('doc_file', file);
            if (sec_file) formData.append('sec_doc', sec_file);

            const billingAddress = {
                name: `${data.firstName} ${data.lastName}`,
                company: "pickupxpress",
                consignor: data.firstName || '',
                address: data.address || '',
                city: data.city || '',
                state: data.state || '',
                pin: data.pincode || '',
                phone: data.mobile || '',
                pan_number: "ABCDE1234F",
                gst_number: "09AAACH7409R1ZZ"
            };
            formData.append('billing_address', JSON.stringify(billingAddress));

            formData.append('insurence', data.insurence === 'carrierRisk' ? 'Carrier' : 'Owner');
            formData.append('fm_pickup', 'True');
            formData.append('retailer_id', '2');

            if (lrnOption === 'manual' && manualLrn) {
                formData.append('lrn', manualLrn);
            }

            const response = await axios.post(Base_Url + b2bAddShipment, formData, {
                headers: {
                    'Content-Type': 'application/json',
                    'token': token,
                    'cus_id': cus_id,
                },
            });

            if (response.data.status === "SUCCESS") {
                setTimeout(() => toast.success("Shipment added successfully ....!", onClose ? { containerId: 'b2b-toast-container', position: "top-right" } : undefined), 1000);
                setTableData(response.data);
                setIsSubmitting(false);
                reset();
                onCreated?.(data.orderId || '', data);
                onClose?.();
            } else {
                showFormError(response.data.message || "Failed to add shipment");
                setIsSubmitting(false);
            }
        } catch (error) {
            if (error.response) {
                const { data } = error.response;
                if (data.messages && typeof data.messages === 'object') {
                    Object.entries(data.messages).forEach(([field, msg]) => showFormError(msg, field));
                } else if (data.message) {
                    showFormError(data.message);
                } else {
                    showFormError(`Failed to add shipment (Error ${error.response.status})`);
                }
                setIsSubmitting(false);
            } else if (error.request) {
                showFormError("No response from server. Please check your network.");
                setIsSubmitting(false);
            } else {
                showFormError("An unexpected error occurred.");
                setIsSubmitting(false);
            }
        }
    };

    const onErrors = (errors) => {
        setFormErrors([]);
        const firstErrorKey = Object.keys(errors)[0];
        if (firstErrorKey && errors[firstErrorKey]) {
            showFormError(errors[firstErrorKey].message || "This field is required", firstErrorKey);
        }
    };

    const handleTotalBoxesChange = (e) => {
        const value = parseInt(e.target.value) || 0;
        setTotalBoxes(value);
        setRemainingBoxes(value);
        setBoxSets([]);
    };

    const handleAddBoxSet = () => {
        if (remainingBoxes > 0) {
            setBoxSets([...boxSets, { quantity: remainingBoxes, length: '', width: '', height: '' }]);
            setRemainingBoxes(0);
        }
    };

    const handleBoxSetChange = (index, field, value) => {
        const newBoxSets = [...boxSets];
        const updatedBoxSet = { ...newBoxSets[index] };

        if (field === 'quantity') {
            const newQuantity = parseInt(value) || 0;
            const otherBoxesQuantity = boxSets.reduce((sum, box, i) =>
                i !== index ? sum + (parseInt(box.quantity) || 0) : sum, 0
            );

            if (newQuantity + otherBoxesQuantity <= totalBoxes) {
                updatedBoxSet.quantity = newQuantity;
                newBoxSets[index] = updatedBoxSet;
                setBoxSets(newBoxSets);
                setRemainingBoxes(totalBoxes - (newQuantity + otherBoxesQuantity));
            }
        } else {
            updatedBoxSet[field] = value;
            newBoxSets[index] = updatedBoxSet;
            setBoxSets(newBoxSets);
        }
    };

    const handleDeleteBoxSet = (index) => {
        const deletedQuantity = boxSets[index].quantity || 0;
        const newBoxSets = boxSets.filter((_, i) => i !== index);
        setBoxSets(newBoxSets);
        const totalUsedBoxes = newBoxSets.reduce((sum, box) => sum + (box.quantity || 0), 0);
        setRemainingBoxes(totalBoxes - totalUsedBoxes);
    };

    const fetchByPhone = async (phone) => {
        try {
            setLoadingPhone(true);
            const res = await fetch(
                `https://api.pickupxpress.com/B2B/shipment/phonefilter?phone=${phone}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        cus_id: localStorage.getItem("cus_id"),
                        token: localStorage.getItem("token"),
                    },
                }
            );

            const json = await res.json();
            if (json.status === "SUCCESS") {
                setPhoneResults(json.data || []);
                setShowDropdown(true);
            } else {
                setPhoneResults([]);
                setShowDropdown(false);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingPhone(false);
        }
    };

    const handleSelectCustomer = (item) => {
        const [firstName, ...lastName] = item.consignee_name.split(" ");
        setValue("firstName", firstName || "");
        setValue("lastName", lastName.join(" ") || "");
        setValue("email", item.email || "");
        setValue("address", item.address || "");
        setValue("city", item.city || "");
        setValue("state", item.state || "");
        setValue("pincode", item.zip || "");
        setValue("mobile", item.phone || "");
        setShowDropdown(false);
    };

    const handleWarehouseSelect = (selectedValue) => {
        const selectedItem = wareHouseList.find((item) => item.value === selectedValue);
        if (selectedItem) {
            setwareHouseId(selectedItem.value);
            setwareHousePincode(selectedItem.pincode);
            setValue("warehouse", selectedValue);
            trigger("warehouse");
        }
    };

    const inputBase = `w-full pl-8 pr-3 py-2 text-xs font-medium rounded-xl border transition-all duration-200 outline-none
        placeholder:text-slate-400 dark:placeholder:text-slate-500
        ${isDarkMode
            ? "bg-white/[0.04] border-white/10 text-white focus:bg-[#161a29] focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 hover:border-white/20"
            : "bg-slate-50/80 border-slate-200 text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15 hover:border-slate-300"
        }`;

    const inputPlain = `w-full px-3 py-2 text-xs font-medium rounded-xl border transition-all duration-200 outline-none
        placeholder:text-slate-400 dark:placeholder:text-slate-500
        ${isDarkMode
            ? "bg-white/[0.04] border-white/10 text-white focus:bg-[#161a29] focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 hover:border-white/20"
            : "bg-slate-50/80 border-slate-200 text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/15 hover:border-slate-300"
        }`;

    const labelBase = `block text-[11px] font-semibold mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`;

    return (
        <>
            {/* Backdrop Overlay */}
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-slate-900/40 dark:bg-black/75 backdrop-blur-[3px] z-[100] transition-opacity duration-300
                    ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
            />

            {/* Right Side Sliding Panel */}
            <div
                className={`fixed top-0 right-0 h-full z-[101] w-full sm:w-[560px] md:w-[620px] lg:w-[690px] xl:w-[735px] max-w-full
                    ${isDarkMode
                        ? "bg-[#0c0e17] text-slate-100 border-l border-indigo-500/20 shadow-[0_0_60px_rgba(79,70,229,0.25)]"
                        : "bg-slate-50 text-slate-800 border-l border-slate-200/90 shadow-[0_0_50px_rgba(0,0,0,0.12)]"
                    }
                    shadow-2xl rounded-tl-2xl rounded-bl-2xl transform transition-transform duration-300 ease-in-out
                    flex flex-col overflow-hidden
                    ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
                {/* Header */}
                <div className={`flex items-center justify-between px-5 py-3.5 border-b shrink-0 ${
                    isDarkMode 
                        ? "bg-[#0c0e17]/95 backdrop-blur-md border-white/[0.08]" 
                        : "bg-white/95 backdrop-blur-md border-slate-200/90"
                }`}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 via-purple-600 to-pink-600 flex items-center justify-center text-white shadow-md shadow-indigo-500/20 shrink-0">
                            <FaWarehouse size={18} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-extrabold tracking-wider uppercase ${
                                    isDarkMode 
                                        ? "bg-purple-500/15 text-purple-300 border border-purple-500/30" 
                                        : "bg-purple-50 text-purple-700 border border-purple-200/80"
                                }`}>
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" />
                                    B2B · Cargo & Freight Booking
                                </span>
                            </div>
                            <h1 className={`text-base font-bold tracking-tight ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                                Create Forward Shipment
                            </h1>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close drawer"
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
                            isDarkMode
                                ? "bg-white/[0.06] hover:bg-white/[0.12] text-slate-400 hover:text-white border border-white/[0.08]"
                                : "bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 border border-slate-200"
                        }`}
                    >
                        <RxCross2 size={16} />
                    </button>
                </div>

                <B2BUploadBulk
                    isopen={uploadBulkPopupOpen}
                    onClose={handleCloseUploadBulk}
                />

                {/* Scrollable Form Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 custom-scrollbar">
                    <form id="b2b-forward-shipment-form" onSubmit={handleSubmit(onSubmit, onErrors)} className="space-y-4">
                        {formErrors.length > 0 && (
                            <div role="alert" className={`flex items-start gap-2 rounded-xl border p-3 text-xs font-medium shadow-sm ${
                                isDarkMode ? 'bg-rose-950/40 border-rose-500/40 text-rose-300' : 'bg-rose-50 border-rose-200 text-rose-700'
                            }`}>
                                <ul className="flex-1 space-y-1">
                                    {formErrors.map((err, i) => (
                                        <li key={i} className="flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
                                            <span>{err}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button type="button" onClick={() => setFormErrors([])} className="opacity-60 hover:opacity-100 font-bold" aria-label="Dismiss">×</button>
                            </div>
                        )}

                        {/* SECTION 1: CUSTOMER DETAILS */}
                        <SectionCard
                            number="01"
                            icon={FaUser}
                            iconGradient="bg-gradient-to-tr from-indigo-500 to-violet-600"
                            title="Customer & Destination Details"
                            subtitle="Warehouse origin & consignee drop-off information"
                            badge="Step 1 of 4"
                            isDarkMode={isDarkMode}
                        >
                            <div className="space-y-3">
                                {/* Warehouse - Full Width */}
                                <div>
                                    <label className={labelBase}>Pickup Warehouse <span className="text-rose-500 font-bold">*</span></label>
                                    <SearchableWarehouseSelect
                                        value={wareHouseId}
                                        onChange={handleWarehouseSelect}
                                        options={wareHouseList}
                                        isDarkMode={isDarkMode}
                                        error={errors.warehouse?.message}
                                        placeholder="Search & select pickup warehouse..."
                                    />
                                </div>

                                {/* Names row */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Field icon={FaUser} label="First Name" required htmlFor="firstName" error={errors.firstName?.message} isDarkMode={isDarkMode}>
                                        <input
                                            id="firstName"
                                            {...register("firstName")}
                                            className={`${inputBase} ${errors.firstName ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            type="text"
                                            onChange={handleInputChange}
                                            placeholder="Consignee first name"
                                        />
                                    </Field>

                                    <Field icon={FaUser} label="Last Name" required htmlFor="lastName" error={errors.lastName?.message} isDarkMode={isDarkMode}>
                                        <input
                                            id="lastName"
                                            {...register("lastName")}
                                            className={`${inputBase} ${errors.lastName ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            type="text"
                                            onChange={handleInputChange}
                                            placeholder="Consignee last name"
                                        />
                                    </Field>
                                </div>

                                {/* Contact & LRN row */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <Field icon={FaEnvelope} label="Email Address" htmlFor="email" error={errors.email?.message} isDarkMode={isDarkMode}>
                                        <input
                                            id="email"
                                            {...register("email")}
                                            className={`${inputBase} ${errors.email ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            type="email"
                                            placeholder="name@business.com (optional)"
                                        />
                                    </Field>

                                    <div className="relative">
                                        <Field icon={FaPhoneAlt} label="Mobile Number" required htmlFor="mobile" error={errors.mobile?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="mobile"
                                                type="tel"
                                                maxLength="10"
                                                placeholder="10-digit mobile"
                                                {...register("mobile")}
                                                className={`${inputBase} ${errors.mobile ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                                onChange={(e) => {
                                                    const phone = e.target.value.replace(/\D/g, "");
                                                    setValue("mobile", phone);
                                                    if (phone.length === 10) {
                                                        fetchByPhone(phone);
                                                    } else {
                                                        setShowDropdown(false);
                                                    }
                                                }}
                                            />
                                        </Field>
                                        {showDropdown && phoneResults.length > 0 && (
                                            <div
                                                className={`absolute z-[10000] left-0 right-0 top-full mt-1.5 max-h-48 overflow-y-auto rounded-xl border shadow-xl custom-scrollbar animate-[fadeIn_0.15s_ease-out]
                                                    ${isDarkMode ? "bg-[#141829] border-white/10 shadow-black/60" : "bg-white border-slate-200 shadow-slate-300/50"}`}
                                                onWheel={(e) => e.stopPropagation()}
                                            >
                                                <div className={`px-3 py-1.5 border-b text-[9.5px] font-bold uppercase tracking-wider ${
                                                    isDarkMode ? 'border-white/[0.06] text-slate-400' : 'border-slate-100 text-slate-500'
                                                }`}>
                                                    Suggested Contacts ({phoneResults.length})
                                                </div>
                                                {phoneResults.map((item, index) => (
                                                    <div
                                                        key={index}
                                                        className={`px-3 py-2 text-xs cursor-pointer transition-colors border-b last:border-0 ${
                                                            isDarkMode ? "border-white/[0.04] hover:bg-indigo-600/20" : "border-slate-50 hover:bg-indigo-50/80"
                                                        }`}
                                                        onClick={() => handleSelectCustomer(item)}
                                                    >
                                                        <div className={`font-semibold ${isDarkMode ? "text-indigo-300" : "text-indigo-700"}`}>{item.consignee_name}</div>
                                                        <div className={`text-[10px] truncate ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                                            {item.address}, {item.city} - {item.zip}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {/* LRN Option */}
                                    <div>
                                        <label className={labelBase}>LRN Option</label>
                                        <div className="flex items-center gap-2 pt-0.5">
                                            <label className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer text-xs transition-all ${
                                                lrnOption === 'auto'
                                                    ? (isDarkMode ? 'bg-indigo-500/20 border-indigo-500 text-indigo-300 font-bold' : 'bg-indigo-50 border-indigo-400 text-indigo-700 font-bold')
                                                    : (isDarkMode ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-600')
                                            }`}>
                                                <input
                                                    type="radio"
                                                    name="lrnOption"
                                                    value="auto"
                                                    checked={lrnOption === 'auto'}
                                                    onChange={() => setLrnOption('auto')}
                                                    className="w-3 h-3 accent-indigo-600"
                                                />
                                                <span>Auto</span>
                                            </label>
                                            <label className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border cursor-pointer text-xs transition-all ${
                                                lrnOption === 'manual'
                                                    ? (isDarkMode ? 'bg-indigo-500/20 border-indigo-500 text-indigo-300 font-bold' : 'bg-indigo-50 border-indigo-400 text-indigo-700 font-bold')
                                                    : (isDarkMode ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-600')
                                            }`}>
                                                <input
                                                    type="radio"
                                                    name="lrnOption"
                                                    value="manual"
                                                    checked={lrnOption === 'manual'}
                                                    onChange={() => setLrnOption('manual')}
                                                    className="w-3 h-3 accent-indigo-600"
                                                />
                                                <span>Manual</span>
                                            </label>
                                        </div>
                                        {lrnOption === 'manual' && (
                                            <div className="mt-2">
                                                <input
                                                    type="text"
                                                    name="manualLrn"
                                                    placeholder="Enter custom LRN"
                                                    value={manualLrn}
                                                    onChange={(e) => setManualLrn(e.target.value)}
                                                    className={inputPlain}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Location row */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <Field icon={FaHashtag} label="Pincode" required htmlFor="pincode" error={errors.pincode?.message} isDarkMode={isDarkMode}>
                                        <input
                                            id="pincode"
                                            maxLength="6"
                                            {...register("pincode")}
                                            className={`${inputBase} font-mono ${errors.pincode ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            type="text"
                                            placeholder="6-digit PIN"
                                        />
                                    </Field>

                                    <Field
                                        icon={FaCity}
                                        label="City"
                                        required
                                        htmlFor="city"
                                        endAdornment={city ? <FaCheckCircle size={10} className="text-emerald-500" /> : null}
                                        error={errors.city?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="city"
                                            {...register("city")}
                                            className={`${inputBase} ${errors.city ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            type="text"
                                            onChange={handleInputChange}
                                            placeholder="City"
                                        />
                                    </Field>

                                    <Field
                                        icon={FaFlag}
                                        label="State"
                                        required
                                        htmlFor="state"
                                        endAdornment={state ? <FaCheckCircle size={10} className="text-emerald-500" /> : null}
                                        error={errors.state?.message}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="state"
                                            {...register("state")}
                                            className={`${inputBase} ${errors.state ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            type="text"
                                            onChange={handleInputChange}
                                            placeholder="State"
                                        />
                                    </Field>
                                </div>

                                {/* Address */}
                                <Field icon={FaMapMarkerAlt} label="Full Drop-off Address" required htmlFor="address" error={errors.address?.message} isDarkMode={isDarkMode}>
                                    <input
                                        id="address"
                                        {...register("address")}
                                        className={`${inputBase} ${errors.address ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                        type="text"
                                        placeholder="Complete street, industrial area, landmark"
                                    />
                                </Field>
                            </div>
                        </SectionCard>

                        {/* 2-COLUMN SECTION: ORDER DETAILS & BOX DETAILS */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* SECTION 2: ORDER DETAILS */}
                            <SectionCard
                                number="02"
                                icon={FaShoppingCart}
                                iconGradient="bg-gradient-to-tr from-blue-600 to-cyan-600"
                                title="Order & Risk Info"
                                subtitle="Commercial terms & insurance options"
                                badge="Step 2 of 4"
                                isDarkMode={isDarkMode}
                            >
                                <div className="space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <Field icon={FaHashtag} label="Order ID" required htmlFor="orderId" error={errors.orderId?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="orderId"
                                                {...register("orderId")}
                                                className={`${inputBase} font-mono ${errors.orderId ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                                type="text"
                                                placeholder="Order ID"
                                            />
                                        </Field>

                                        <Field icon={FaClipboardList} label="Product Description" required htmlFor="productDes" error={errors.productDes?.message} isDarkMode={isDarkMode}>
                                            <input
                                                id="productDes"
                                                {...register("productDes")}
                                                className={`${inputBase} ${errors.productDes ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                                type="text"
                                                placeholder="e.g. Industrial Parts"
                                            />
                                        </Field>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <Field icon={FaWallet} label="Payment Mode" required htmlFor="paymentMode" error={errors.paymentMode?.message} isDarkMode={isDarkMode}>
                                            <select
                                                id="paymentMode"
                                                {...register("paymentMode")}
                                                className={`${inputBase} cursor-pointer pr-8 ${errors.paymentMode ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            >
                                                <option value="prepaid" className={isDarkMode ? "bg-[#141829]" : ""}>Prepaid</option>
                                                <option value="cod" className={isDarkMode ? "bg-[#141829]" : ""}>Cash on Delivery (COD)</option>
                                            </select>
                                        </Field>

                                        <Field
                                            icon={FaRupeeSign}
                                            label="COD Amount"
                                            htmlFor="codAmount"
                                            error={errors.codAmount?.message}
                                            endAdornment={
                                                paymentMode === "cod" ? (
                                                    <span className="text-[9px] font-bold text-amber-500 uppercase tracking-wide">COD Active</span>
                                                ) : (
                                                    <span className="text-[9px] text-slate-400">Prepaid = ₹0</span>
                                                )
                                            }
                                            isDarkMode={isDarkMode}
                                        >
                                            <input
                                                id="codAmount"
                                                {...register("codAmount")}
                                                disabled={paymentMode !== "cod"}
                                                className={`${inputBase} font-semibold ${
                                                    paymentMode !== "cod"
                                                        ? "opacity-50 cursor-not-allowed bg-slate-100 dark:bg-white/[0.02]"
                                                        : "border-amber-500/50"
                                                } ${errors.codAmount ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                                type="text"
                                                placeholder={paymentMode === "cod" ? "Enter COD amount" : "₹0 (Prepaid)"}
                                            />
                                        </Field>
                                    </div>

                                    {/* Insurance Section Card */}
                                    <div className={`p-3 rounded-xl border ${
                                        isDarkMode
                                            ? "bg-indigo-950/20 border-indigo-500/25"
                                            : "bg-slate-50 border-slate-200"
                                    }`}>
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <FaShieldAlt className="text-indigo-500 text-xs" />
                                            <h3 className={`text-xs font-bold ${isDarkMode ? "text-white" : "text-slate-800"}`}>
                                                Insure Your Shipment
                                            </h3>
                                        </div>
                                        <p className={`text-[10px] mb-2.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                            Declare transit liability risk coverage terms:
                                        </p>
                                        <div className="space-y-1.5">
                                            <label className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                                                isDarkMode ? 'border-white/[0.06] hover:bg-white/[0.03]' : 'border-slate-200/80 hover:bg-white'
                                            }`}>
                                                <input
                                                    type="radio"
                                                    {...register("insurence")}
                                                    value="ownerRisk"
                                                    defaultChecked
                                                    className="w-3.5 h-3.5 mt-0.5 accent-indigo-600"
                                                />
                                                <div className="text-[10px]">
                                                    <span className={`font-bold block ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                                                        Owner's Risk (Recommended)
                                                    </span>
                                                    <span className={isDarkMode ? "text-slate-400" : "text-slate-500"}>
                                                        Charges applied as per contracted rate sheet
                                                    </span>
                                                </div>
                                            </label>
                                            <label className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
                                                isDarkMode ? 'border-white/[0.06] hover:bg-white/[0.03]' : 'border-slate-200/80 hover:bg-white'
                                            }`}>
                                                <input
                                                    type="radio"
                                                    {...register("insurence")}
                                                    value="carrierRisk"
                                                    className="w-3.5 h-3.5 mt-0.5 accent-indigo-600"
                                                />
                                                <div className="text-[10px]">
                                                    <span className={`font-bold block ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>
                                                        Carrier's Risk
                                                    </span>
                                                    <span className={isDarkMode ? "text-slate-400" : "text-slate-500"}>
                                                        Nominal insurance surcharge added
                                                    </span>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>

                            {/* SECTION 3: BOX DETAILS */}
                            <SectionCard
                                number="03"
                                icon={FaBox}
                                iconGradient="bg-gradient-to-tr from-amber-500 to-orange-500"
                                title="Box & Dimension Breakdown"
                                subtitle="Total boxes, cargo weight & set dimensions"
                                badge="Step 3 of 4"
                                isDarkMode={isDarkMode}
                            >
                                <div className="space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                        <Field icon={FaBoxOpen} label="Total Boxes" required isDarkMode={isDarkMode}>
                                            <input
                                                type="number"
                                                min="1"
                                                onChange={handleTotalBoxesChange}
                                                className={`${inputBase} font-bold`}
                                                placeholder="e.g. 5"
                                            />
                                        </Field>

                                        <Field icon={FaWeightHanging} label="Total Weight" required isDarkMode={isDarkMode}>
                                            <input
                                                type="number"
                                                step="0.01"
                                                {...register("total_weight")}
                                                className={`${inputBase} font-bold`}
                                                placeholder="Weight (kg)"
                                            />
                                        </Field>

                                        <div>
                                            <label className={labelBase}>Unit</label>
                                            <select className={`${inputPlain} cursor-pointer`}>
                                                <option value="cm">cm (Centimeters)</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Dynamic Box Sets */}
                                    <div className="space-y-2">
                                        {boxSets.map((boxSet, index) => (
                                            <div
                                                key={index}
                                                className={`p-2.5 rounded-xl border transition-all ${
                                                    isDarkMode
                                                        ? "bg-white/[0.02] border-white/[0.08]"
                                                        : "bg-slate-50 border-slate-200"
                                                }`}
                                            >
                                                <div className="grid grid-cols-5 gap-2 items-center">
                                                    <div>
                                                        <label className="text-[9px] font-bold text-slate-500 uppercase block mb-0.5">Qty</label>
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            pattern="[0-9]*"
                                                            value={boxSet.quantity ?? ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'quantity', e.target.value.replace(/[^0-9]/g, ''))}
                                                            className={`${inputPlain} font-bold text-center !py-1`}
                                                            placeholder="0"
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[9px] font-bold text-slate-500 uppercase block mb-0.5">L (cm)</label>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={boxSet.length ?? ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'length', e.target.value.replace(/[^0-9.]/g, ''))}
                                                            className={`${inputPlain} text-center !py-1`}
                                                            placeholder="0"
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[9px] font-bold text-slate-500 uppercase block mb-0.5">W (cm)</label>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={boxSet.width ?? ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'width', e.target.value.replace(/[^0-9.]/g, ''))}
                                                            className={`${inputPlain} text-center !py-1`}
                                                            placeholder="0"
                                                            required
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[9px] font-bold text-slate-500 uppercase block mb-0.5">H (cm)</label>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={boxSet.height ?? ''}
                                                            onChange={(e) => handleBoxSetChange(index, 'height', e.target.value.replace(/[^0-9.]/g, ''))}
                                                            className={`${inputPlain} text-center !py-1`}
                                                            placeholder="0"
                                                            required
                                                        />
                                                    </div>
                                                    <div className="flex items-end justify-center pt-3.5">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteBoxSet(index)}
                                                            className="w-7 h-7 rounded-lg flex items-center justify-center text-rose-500 hover:bg-rose-500/10 transition-colors"
                                                            title="Delete box set"
                                                        >
                                                            <FaTrash size={11} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        <div className="flex items-center justify-between pt-1">
                                            <button
                                                type="button"
                                                onClick={handleAddBoxSet}
                                                disabled={remainingBoxes === 0}
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-xl border transition-all ${
                                                    remainingBoxes === 0
                                                        ? (isDarkMode ? 'bg-white/5 border-white/5 text-slate-500 cursor-not-allowed' : 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed')
                                                        : (isDarkMode
                                                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white border-transparent hover:from-amber-600 hover:to-orange-600 shadow-md shadow-amber-500/20 active:scale-95'
                                                            : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white border-transparent hover:from-amber-600 hover:to-orange-600 shadow-md shadow-amber-500/20 active:scale-95')
                                                }`}
                                            >
                                                <FaPlus size={10} />
                                                <span>Add Box Set</span>
                                            </button>
                                            {remainingBoxes > 0 && (
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                                    isDarkMode ? "bg-amber-500/15 text-amber-300" : "bg-amber-50 text-amber-700 border border-amber-200"
                                                }`}>
                                                    Unassigned Boxes: {remainingBoxes}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>
                        </div>

                        {/* SECTION 4: INVOICE DETAILS */}
                        <SectionCard
                            number="04"
                            icon={FaFileInvoice}
                            iconGradient="bg-gradient-to-tr from-emerald-500 to-teal-600"
                            title="Invoice & Commercial Compliance"
                            subtitle="Commercial invoice number, e-waybill & declared value"
                            badge="Step 4 of 4"
                            isDarkMode={isDarkMode}
                        >
                            <div className="space-y-3">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <Field icon={FaHashtag} label="Invoice Number" required htmlFor="invoiceNumber" error={errors.invoiceNumber?.message} isDarkMode={isDarkMode}>
                                        <input
                                            id="invoiceNumber"
                                            {...register("invoiceNumber")}
                                            className={`${inputBase} font-mono ${errors.invoiceNumber ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            type="text"
                                            placeholder="INV-00129"
                                        />
                                    </Field>

                                    <Field
                                        icon={FaHashtag}
                                        label="E-Waybill No"
                                        htmlFor="eWaybillNo"
                                        error={errors.eWaybillNo?.message}
                                        endAdornment={<span className="text-[9.5px] text-slate-400">Required if &gt; ₹50K</span>}
                                        isDarkMode={isDarkMode}
                                    >
                                        <input
                                            id="eWaybillNo"
                                            {...register("eWaybillNo")}
                                            className={`${inputBase} font-mono ${errors.eWaybillNo ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            type="text"
                                            placeholder="12-digit E-Waybill"
                                            maxLength={12}
                                        />
                                    </Field>

                                    <Field icon={FaRupeeSign} label="Total Invoice Amount" required htmlFor="invoiceAmount" error={errors.invoiceAmount?.message} isDarkMode={isDarkMode}>
                                        <input
                                            id="invoiceAmount"
                                            {...register("invoiceAmount")}
                                            className={`${inputBase} font-bold ${errors.invoiceAmount ? '!border-rose-500/80 !ring-rose-500/20' : ''}`}
                                            type="text"
                                            placeholder="0.00"
                                        />
                                    </Field>
                                </div>
                            </div>
                        </SectionCard>
                    </form>
                </div>

                {/* Sticky Action Footer */}
                <div className={`sticky bottom-0 z-30 px-5 py-3.5 border-t shrink-0 flex items-center justify-between gap-3 ${
                    isDarkMode 
                        ? "bg-[#0c0e17]/95 backdrop-blur-md border-white/[0.08]" 
                        : "bg-white/95 backdrop-blur-md border-slate-200/90"
                }`}>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className={`px-4 py-2.5 rounded-xl border text-xs font-semibold transition-all duration-200 ${
                            isDarkMode
                                ? "border-white/10 text-slate-300 hover:bg-white/[0.06] hover:text-white"
                                : "border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        }`}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="b2b-forward-shipment-form"
                        disabled={isSubmitting}
                        className={`flex-1 py-2.5 px-5 rounded-xl font-bold text-xs text-white flex items-center justify-center gap-2 shadow-lg transition-all duration-200 ${
                            isDarkMode
                                ? "bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:from-blue-500 hover:via-indigo-500 hover:to-violet-500 shadow-indigo-500/25 active:scale-[0.99]"
                                : "bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:from-blue-700 hover:via-indigo-700 hover:to-violet-700 shadow-blue-500/25 active:scale-[0.99]"
                        } ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""}`}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center gap-2">
                                <Spinner /> Creating Shipment...
                            </span>
                        ) : (
                            <span className="flex items-center gap-2">
                                <FaWarehouse size={13} />
                                <span>Create & Book B2B Shipment</span>
                            </span>
                        )}
                    </button>
                </div>
            </div>

            <B2BViewRateForword
                isopen={isPopupOpen}
                onClose={handleClose}
                data={tableData}
                reqBody={reqBody}
            />
        </>
    );
}